/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { TCards } from '@lbs/shared-rp-types'
import { useActor, useSelector } from '@xstate/react'
import { motion } from 'framer-motion'
import { observer } from 'mobx-react-lite'
import { FC, useCallback } from 'react'
import { rpMediaQueries } from '../../core/theme'
import { rpSelectors } from '../../machines/RussianPoker/selectors'
import { ERpEvent } from '../../machines/RussianPoker/types'
import { useRpService } from '../../providers/RussianPokerProvider'
import { BoxStore } from '../../stores/RussianPoker/Box.store'
import { BetsDataView } from '../BetsDataView'
import { BgMixin } from '../BettingBox/styles'
import CardHolder, { mapBoxToCardHolderVM } from '../CardHolder'
import DecisionBoxStatus from '../DecisionBoxStatus'
import DecisionButtons from '../DecisionButtons'
import DecisionBuyDealerGame from '../DecisionBuyDealerGame'
import DecisionInsurance from '../DecisionInsurance'
import { TotalDepositBox } from '../TotalDepositBox'

type DecisionBoxProps = {
  box: BoxStore
}

export const StyledDecisionBox = styled.div`
  ${BgMixin};
  border-radius: 5px;
  padding: 4px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  position: relative;
  min-width: 100%;
  transition: width 0.3s ease, height 0.3s ease, box-shadow 0.3s ease;

  ${rpMediaQueries.tablet} {
    padding: 8px;
  }
`

// Permanent overlay slot anchored to the left edge of StyledDecisionBox.
// Always mounted — animation-only visibility prevents first-frame geometry jumps.
const InsuranceSlot = styled(motion.div)`
  position: absolute;
  top: 0;
  right: 100%;
  height: 100%;
`

const DecisionBox: FC<DecisionBoxProps> = observer(({ box }) => {
  const { sumStake, decision, ui, isSettledPhase, isFinishedPhase } = box

  // isMyTurn — true when activeBoxReady|activeBoxRevealed: detail view + card flip can start.
  // isInteractive — true only when activeBoxRevealed: card flip is done, controls are shown.
  const rpService = useRpService()
  const isMyTurn = useSelector(
    rpService,
    rpSelectors.isDetailViewForBox(box.seatNumber)
  )
  const isInteractive = useSelector(
    rpService,
    rpSelectors.isInteractiveForBox(box.seatNumber)
  )
  const isSettled = useSelector(rpService, rpSelectors.isSettlementPhase)
  const isFinished = useSelector(rpService, rpSelectors.isFinishedPhase)

  // Called by the last FlipCard after Phase 2 (scaleX 0→1) actually completes.
  // Sends ACTIVE_BOX_REVEAL_DONE only while still in activeBoxReady — safe to send
  // unconditionally since the event is discarded in any other state.
  const handleRevealComplete = useCallback(() => {
    rpService.send({ type: ERpEvent.ActiveBoxRevealDone })
  }, [rpService])

  const [machineState] = useActor(ui.service)
  const isInsuranceOpen = isInteractive && machineState.matches('insurance')

  const isDetailView = isMyTurn || isSettledPhase || isFinishedPhase

  // In non-detail decisions view keep cards face-down.
  // Otherwise the same box briefly shows real cards in compact mode,
  // then flips back to XX when DetailCardsView mounts.
  const cardsForView = isDetailView
    ? box.cards
    : box.cards.map(() => 'XX' as TCards)

  const cardHolderVM = mapBoxToCardHolderVM({
    cards: cardsForView,
    handName: box.hand.handName,
    extraHandName: box.hand.extra?.handName,
    isBuySixCardAvailable: box.isBuySixCardAvailable,
    isSwitchAvailable: box.isSwitchAvailable,
    hasSelectedCardsForSwitch: machineState.context.cardsForSwitch.length > 0,
    darkenedCards: machineState.context.cardsForSwitch,
    btnDecision: machineState.value as string,
    onToggleCardsForSwitch: ui.toggleCardsForSwitch,
    onSetSixCardDecision: () => ui.setSixCardDecision(),
    isDetailView,
    isMyTurn,
    isInteractive,
    animateCombination: isInteractive,
    showCombination: isInteractive || isSettledPhase || isFinishedPhase,
    onRevealComplete: isMyTurn ? handleRevealComplete : undefined,
  })

  const redShadow =
    decision === 'pass' ||
    (box.result.outcome === 'lose' &&
      css`
        box-shadow: 0 4px 20px 0 rgba(233, 1, 70, 0.5),
          0 0 8px 0 rgba(178, 155, 106, 0.13),
          0 0 25px 0 rgba(255, 196, 69, 0.1);
      `)

  const greenShadow =
    box.result.outcome === 'win' &&
    css`
      box-shadow: 0 4px 20px 0 rgba(61, 255, 57, 0.5),
        0 0 8px 0 rgba(178, 155, 106, 0.13), 0 0 25px 0 rgba(255, 196, 69, 0.1);
    `

  return (
    <motion.div
      layout
      transition={{ layout: { duration: 0.1, ease: 'easeOut' } }}
      style={{ minWidth: '100%' }}>
      <StyledDecisionBox css={[greenShadow, redShadow]}>
        {/* Insurance slot — always mounted, outside normal flow. Animate-only visibility
            prevents first-frame reflow that caused the "crushed then snapped" artifact. */}
        <InsuranceSlot
          initial={false}
          animate={
            isInsuranceOpen ? { x: 0, opacity: 1 } : { x: 18, opacity: 0 }
          }
          transition={{ duration: 0.3, ease: 'easeOut' }}
          style={{ pointerEvents: isInsuranceOpen ? 'auto' : 'none' }}>
          <DecisionInsurance box={box} />
        </InsuranceSlot>

        <TotalDepositBox deposit={sumStake} />

        <BetsDataView box={box} isInteractive={isInteractive} />

        <DecisionBoxStatus
          box={box}
          isInteractive={isInteractive}
          isSettled={isSettled}
          isFinished={isFinished}
        />

        <CardHolder viewModel={cardHolderVM} />

        {isMyTurn && (
          <motion.div
            initial={false}
            animate={{
              opacity: isInteractive ? 1 : 0,
              y: isInteractive ? 0 : 6,
            }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            style={{ pointerEvents: isInteractive ? 'auto' : 'none' }}>
            <DecisionButtons box={box} />
          </motion.div>
        )}
        <DecisionBuyDealerGame box={box} />
      </StyledDecisionBox>
    </motion.div>
  )
})

export default DecisionBox
